import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useUserRole } from "../../../context/UserRoleContext";
import DashboardLayout from "../../../components/common/DashboardLayout";
import { useArtist } from "../../../context/ArtistContext";
import ShowCardModal from "../components/ShowCardModal";
import { formatDateForInput } from "../../../utils/calendarDate";
import {
  civilDateTodayInZone,
  normalizeBarTimeZone,
} from "../../../utils/civilDateTime.js";

const formatTime = (time) => {
  if (!time) return "";
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "pm" : "am";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes}${ampm}`;
};

const parseTimeToMinutes = (time) => {
  if (!time) return null;
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours, 10);
  const minute = parseInt(minutes || "0", 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }
  return hour * 60 + minute;
};

const Calendar = () => {
  const { loading } = useUserRole();
  const { shows, showsLoading, refreshShows } = useArtist();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isShowCardModalOpen, setIsShowCardModalOpen] = useState(false);
  const [selectedShow, setSelectedShow] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);

  // Listen for shows updated event to refresh shows
  useEffect(() => {
    const handleShowsUpdated = () => {
      refreshShows();
    };
    window.addEventListener("showsUpdated", handleShowsUpdated);
    return () => {
      window.removeEventListener("showsUpdated", handleShowsUpdated);
    };
  }, [refreshShows]);

  const normalizedShows = useMemo(() => {
    if (!shows?.length) return [];
    return shows.map((show) => ({
      ...show,
      date: formatDateForInput(show.date),
    }));
  }, [shows]);

  const showsByDate = useMemo(() => {
    if (!normalizedShows.length) return new Map();
    const map = new Map();

    normalizedShows.forEach((show) => {
      if (!map.has(show.date)) {
        map.set(show.date, []);
      }
      map.get(show.date).push(show);
    });

    map.forEach((list) => {
      list.sort((a, b) => {
        const aStart = parseTimeToMinutes(a.start_time) ?? 0;
        const bStart = parseTimeToMinutes(b.start_time) ?? 0;
        return aStart - bStart;
      });
    });

    return map;
  }, [normalizedShows]);

  const getShowsForDay = (date) => {
    if (!date) return [];
    const dateStr = formatDateForInput(date);
    return showsByDate.get(dateStr) ?? [];
  };

  const selectedDayShows = useMemo(() => {
    if (!selectedDay) return [];
    return getShowsForDay(selectedDay);
  }, [selectedDay, showsByDate]);

  // Refresh earnings when show is updated
  const handleEarningsUpdate = useCallback(() => {
    refreshShows();
  }, [refreshShows]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  // Calendar navigation functions
  const moveMonthBackward = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const moveMonthForward = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  // Generate month dates with proper calendar grid (including leading/trailing days)
  const generateMonthDates = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Get day of week (0 = Sunday, 6 = Saturday)
    const firstDayOfWeek = firstDay.getDay();
    const lastDate = lastDay.getDate();

    const dates = [];

    // Add leading days from previous month
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      dates.push({ date, isCurrentMonth: false });
    }

    // Add days of current month
    for (let i = 1; i <= lastDate; i++) {
      const date = new Date(year, month, i);
      dates.push({ date, isCurrentMonth: true });
    }

    // Add trailing days to complete the grid
    const remainingDays = 7 - (dates.length % 7);
    if (remainingDays < 7) {
      for (let i = 1; i <= remainingDays; i++) {
        const date = new Date(year, month + 1, i);
        dates.push({ date, isCurrentMonth: false });
      }
    }

    return dates;
  };

  const monthDates = generateMonthDates();
  const deviceZone =
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : undefined;
  const calendarTodayYmd = civilDateTodayInZone(
    normalizeBarTimeZone(deviceZone)
  );
  const isToday = (date) => formatDateForInput(date) === calendarTodayYmd;

  // Get month and year display
  const getMonthDisplay = () => {
    return currentDate.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  const openDayModal = (dateObj) => {
    if (!dateObj.isCurrentMonth) return;
    setSelectedDay(dateObj.date);
    setIsDayModalOpen(true);
  };

  const closeDayModal = () => {
    setIsDayModalOpen(false);
    setSelectedDay(null);
  };

  const handleDayKeyDown = (dateObj, e) => {
    if (!dateObj.isCurrentMonth) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openDayModal(dateObj);
    }
  };

  // Handle show click
  const handleShowClick = (show) => {
    setSelectedShow({
      ...show,
      date: show.date,
    });
    setIsShowCardModalOpen(true);
  };

  const openShowFromDayModal = (show) => {
    closeDayModal();
    handleShowClick(show);
  };

  const closeShowModal = () => {
    setIsShowCardModalOpen(false);
    setSelectedShow(null);
  };

  return (
    <>
      <DashboardLayout>
        <div className="artist-dashboard">
          {/* Calendar Section */}
          <div
            className="calendar-section"
            aria-busy={showsLoading ? "true" : "false"}
          >
            {showsLoading && (
              <div className="calendar-loading-overlay" role="status">
                <div className="calendar-loading-indicator">
                  <span
                    className="calendar-loading-spinner"
                    aria-hidden="true"
                  />
                  <span className="calendar-loading-text">
                    Refreshing schedule…
                  </span>
                </div>
              </div>
            )}
            {/* Calendar Navigation Buttons */}
            <div className="calendar-nav">
              <div className="calendar-nav-content">
                <div className="nav-btn-group">
                  <button
                    type="button"
                    className="nav-btn month-btn"
                    onClick={moveMonthBackward}
                  >
                    ← Month
                  </button>
                </div>
                <div className="calendar-month-display">
                  {getMonthDisplay()}
                </div>
                <div className="nav-btn-group">
                  <button
                    type="button"
                    className="nav-btn month-btn"
                    onClick={moveMonthForward}
                  >
                    Month →
                  </button>
                </div>
              </div>
            </div>

            {/* Weekday Headers */}
            <div className="calendar-weekday-headers">
              <div className="weekday-header">Sun</div>
              <div className="weekday-header">Mon</div>
              <div className="weekday-header">Tue</div>
              <div className="weekday-header">Wed</div>
              <div className="weekday-header">Thu</div>
              <div className="weekday-header">Fri</div>
              <div className="weekday-header">Sat</div>
            </div>

            {/* Month Calendar Grid */}
            <div className="month-calendar">
              {monthDates.map((dateObj, index) => {
                const showsForDay = dateObj.isCurrentMonth
                  ? getShowsForDay(dateObj.date)
                  : [];
                const showCount = showsForDay.length;
                const pendingCount = showsForDay.filter(
                  (s) => s.status === "pending",
                ).length;

                const shortDateLabel = dateObj.date.toLocaleDateString(
                  "en-US",
                  {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  },
                );

                const dayAriaLabel = !dateObj.isCurrentMonth
                  ? undefined
                  : showCount === 0
                    ? `${shortDateLabel}, no shows`
                    : `${shortDateLabel}, ${showCount} show${
                        showCount !== 1 ? "s" : ""
                      }${pendingCount > 0 ? ", includes pending" : ""}. Open day details.`;

                return (
                  <div
                    key={index}
                    className={`calendar-day-cell ${
                      isToday(dateObj.date) ? "today" : ""
                    } ${!dateObj.isCurrentMonth ? "other-month" : ""}`}
                    onClick={() => openDayModal(dateObj)}
                    onKeyDown={(e) => handleDayKeyDown(dateObj, e)}
                    role={dateObj.isCurrentMonth ? "button" : undefined}
                    tabIndex={dateObj.isCurrentMonth ? 0 : undefined}
                    aria-label={dayAriaLabel}
                  >
                    <div className="day-header">
                      <div className="day-number">{dateObj.date.getDate()}</div>
                    </div>

                    {dateObj.isCurrentMonth && showCount > 0 && (
                      <div
                        className="calendar-day-mobile-summary"
                        aria-hidden="true"
                      >
                        <span className="calendar-day-has-dot" />
                        <span className="calendar-day-show-count">
                          {showCount}
                        </span>
                        {pendingCount > 0 && (
                          <span
                            className="calendar-day-pending-indicator"
                            title="Includes pending show"
                          />
                        )}
                      </div>
                    )}

                    {dateObj.isCurrentMonth && (
                      <div className="day-shows">
                        {showsForDay.length === 0 ? (
                          <div className="no-shows-message">
                            <span className="no-show-text">No shows</span>
                          </div>
                        ) : (
                          showsForDay.map((show) => {
                            const showKey =
                              show.show_id ??
                              `${show.date}-${show.start_time}-${show.bar_id}`;
                            return (
                              <div
                                key={showKey}
                                className={`artist-show-card ${
                                  show.status === "pending" ? "pending" : ""
                                }`}
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShowClick(show);
                                }}
                                onKeyDown={(event) => {
                                  if (
                                    event.key === "Enter" ||
                                    event.key === " "
                                  ) {
                                    event.stopPropagation();
                                    handleShowClick(show);
                                  }
                                }}
                              >
                                <div className="artist-show-card-header">
                                  <span className="artist-show-card-time">
                                    {formatTime(show.start_time)} -{" "}
                                    {formatTime(show.end_time)}
                                  </span>
                                  {show.status === "pending" && (
                                    <span className="pending-badge">
                                      ⏳ Pending
                                    </span>
                                  )}
                                </div>
                                <div className="artist-show-card-content">
                                  <span className="artist-show-card-stage-name">
                                    {show.stage_name || "Artist"}
                                  </span>
                                  <span className="artist-show-card-venue">
                                    {show.bar_name || "Venue"}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </DashboardLayout>

      {isDayModalOpen && selectedDay && (
        <>
          <div className="modal-overlay" onClick={closeDayModal} />
          <div className="modal-container calendar-day-detail-modal">
            <div className="modal-header">
              <h2 className="modal-title">
                {selectedDay.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeDayModal}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="modal-content artist-day-detail-modal-content">
              {selectedDayShows.length === 0 ? (
                <p className="artist-day-detail-empty">No shows scheduled.</p>
              ) : (
                <ul className="artist-day-detail-list">
                  {selectedDayShows.map((show) => {
                    const showKey =
                      show.show_id ??
                      `${show.date}-${show.start_time}-${show.bar_id}`;
                    const isPending = show.status === "pending";
                    return (
                      <li key={showKey} className="artist-day-detail-item">
                        <button
                          type="button"
                          className="artist-day-detail-show-row"
                          onClick={() => openShowFromDayModal(show)}
                        >
                          <span className="artist-day-detail-show-time">
                            {formatTime(show.start_time)} –{" "}
                            {formatTime(show.end_time)}
                          </span>
                          <span className="artist-day-detail-show-venue">
                            {show.bar_name || "Venue"}
                          </span>
                          {isPending && (
                            <span className="artist-day-detail-pending">
                              Pending
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      )}

      <ShowCardModal
        isOpen={isShowCardModalOpen}
        onClose={closeShowModal}
        showId={selectedShow?.show_id}
        fallbackShow={selectedShow}
        onEarningsUpdate={handleEarningsUpdate}
      />
    </>
  );
};

export default Calendar;
